import React, { useState } from 'react';
import {
  Modal,
  Button,
  Space,
  Input,
  Divider,
  message,
  Typography,
} from 'antd';
import {
  CopyOutlined,
  MailOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { generateScheduleConfirmationUrl } from '../../shared/utils/schedule-confirmation-utils';
import { useI18n } from '../../shared/i18n';

const { Text } = Typography;

interface ISendScheduleConfirmationProps {
  open: boolean;
  onClose: () => void;
  scheduleId: string;
  token: string;
  clientEmail?: string;
  clientPhone?: string;
  clientName?: string;
  onSendEmail?: (email: string) => Promise<void>;
  onSendWhatsapp?: (phone: string, link: string) => Promise<void>;
  loading?: boolean;
}

export const SendScheduleConfirmation: React.FC<
  ISendScheduleConfirmationProps
> = ({
  open,
  onClose,
  scheduleId,
  token,
  clientEmail,
  clientPhone,
  clientName,
  onSendEmail,
  onSendWhatsapp,
  loading = false,
}) => {
  const { t } = useI18n();
  const confirmationUrl = generateScheduleConfirmationUrl(scheduleId, token);
  const resolvedClientName =
    clientName || t('sendScheduleConfirmation.defaultClientName');
  const [emailAddress, setEmailAddress] = useState(clientEmail || '');
  const [phoneNumber, setPhoneNumber] = useState(clientPhone || '');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingWhatsapp, setSendingWhatsapp] = useState(false);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(confirmationUrl);
    message.success(t('sendScheduleConfirmation.messages.copied'));
  };

  const handleSendEmail = async () => {
    if (!emailAddress) {
      message.error(t('sendScheduleConfirmation.messages.emailRequired'));
      return;
    }

    try {
      setSendingEmail(true);
      if (onSendEmail) {
        await onSendEmail(emailAddress);
      }
      message.success(t('sendScheduleConfirmation.messages.emailSent'));
    } catch (error: any) {
      message.error(
        error?.message || t('sendScheduleConfirmation.messages.emailError'),
      );
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendWhatsapp = async () => {
    if (!phoneNumber) {
      message.error(t('sendScheduleConfirmation.messages.phoneRequired'));
      return;
    }

    try {
      setSendingWhatsapp(true);
      if (onSendWhatsapp) {
        await onSendWhatsapp(phoneNumber, confirmationUrl);
      }
      message.success(t('sendScheduleConfirmation.messages.whatsappSent'));
    } catch (error: any) {
      message.error(
        error?.message || t('sendScheduleConfirmation.messages.whatsappError'),
      );
    } finally {
      setSendingWhatsapp(false);
    }
  };

  return (
    <Modal
      title={t('sendScheduleConfirmation.title')}
      open={open}
      onCancel={onClose}
      width={600}
      footer={[
        <Button key="close" onClick={onClose}>
          {t('sendScheduleConfirmation.close')}
        </Button>,
      ]}
    >
      <div style={{ marginBottom: '24px' }}>
        <Text strong>
          {t('sendScheduleConfirmation.to')}: {resolvedClientName}
        </Text>
        <Divider style={{ margin: '12px 0' }} />
      </div>

      {/* SETOR LINK */}
      <div style={{ marginBottom: '24px' }}>
        <h4>🔗 {t('sendScheduleConfirmation.sections.link')}</h4>
        <Input.TextArea
          value={confirmationUrl}
          readOnly
          rows={3}
          style={{ marginBottom: '12px', fontSize: '12px' }}
        />
        <Button
          type="primary"
          icon={<CopyOutlined />}
          onClick={handleCopyLink}
          block
        >
          {t('sendScheduleConfirmation.actions.copyLink')}
        </Button>
        <Text
          type="secondary"
          style={{ display: 'block', marginTop: '8px', fontSize: '12px' }}
        >
          ℹ️ {t('sendScheduleConfirmation.hints.expiry')}
        </Text>
      </div>

      <Divider />

      {/* SETOR EMAIL */}
      <div style={{ marginBottom: '24px' }}>
        <h4>📧 {t('sendScheduleConfirmation.sections.email')}</h4>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input
            type="email"
            placeholder={t('sendScheduleConfirmation.fields.emailPlaceholder')}
            prefix={<MailOutlined />}
            value={emailAddress}
            onChange={(e) => setEmailAddress(e.target.value)}
            disabled={sendingEmail || loading}
          />
          <Button
            icon={<MailOutlined />}
            onClick={handleSendEmail}
            loading={sendingEmail || loading}
            disabled={!emailAddress || sendingEmail || loading}
            block
          >
            {sendingEmail
              ? t('sendScheduleConfirmation.actions.sending')
              : t('sendScheduleConfirmation.actions.sendEmail')}
          </Button>
        </Space>
      </div>

      <Divider />

      {/* SETOR WHATSAPP */}
      <div style={{ marginBottom: '24px' }}>
        <h4>💬 {t('sendScheduleConfirmation.sections.whatsapp')}</h4>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input
            placeholder={t('sendScheduleConfirmation.fields.phonePlaceholder')}
            prefix={<WhatsAppOutlined />}
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            disabled={sendingWhatsapp || loading}
            pattern="[0-9+]+"
          />
          <Button
            icon={<WhatsAppOutlined />}
            onClick={handleSendWhatsapp}
            loading={sendingWhatsapp || loading}
            disabled={!phoneNumber || sendingWhatsapp || loading}
            style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
            type="primary"
            block
          >
            {sendingWhatsapp
              ? t('sendScheduleConfirmation.actions.sending')
              : t('sendScheduleConfirmation.actions.sendWhatsapp')}
          </Button>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            💡 {t('sendScheduleConfirmation.hints.phoneFormat')}
          </Text>
        </Space>
      </div>

      <Divider />

      {/* INFORMAÇÕES */}
      <div
        style={{
          backgroundColor: '#fafafa',
          padding: '12px',
          borderRadius: '4px',
        }}
      >
        <Text strong>ℹ️ {t('sendScheduleConfirmation.sections.info')}</Text>
        <ul
          style={{
            marginBottom: 0,
            marginTop: '8px',
            fontSize: '12px',
            color: '#595959',
          }}
        >
          <li>{t('sendScheduleConfirmation.infoList.valid')}</li>
          <li>{t('sendScheduleConfirmation.infoList.confirmOrCancel')}</li>
          <li>{t('sendScheduleConfirmation.infoList.confirmationNotice')}</li>
          <li>{t('sendScheduleConfirmation.infoList.singleUse')}</li>
        </ul>
      </div>
    </Modal>
  );
};

export default SendScheduleConfirmation;
